import EventBus from '@mvps-genesis/event-bus';

/**
 * @type {Record<string, {products: any[], timer: number}>}
 */
const debouncedImpressions = {};

/**
 * A class to support Google Tag Manager & the dataLayer
 * @class
 */
export default class GTM {

	/**
	 * @param {Object} context
	 * @returns {void}
	 */
	constructor (context) {
		this.context = context;

		this.classes = {
			eventClick: 'js-gtm-event-click',
			impression: 'js-gtm-impression',
			list: 'js-gtm-list',
			promo: 'js-gtm-promo',
			promoLink: 'js-gtm-promo-link'
		};

		this.attributes = {
			eventClick: 'data-event-click-event',
			impression: 'data-impression-event-sent',
			productClick: 'data-product-click-event',
			promo: 'data-promo-event-sent',
			promoLink: 'data-promo-click-event'
		};
	}

	/**
	 * Instantiate the GTM class. This should only ever be called once.
	 * @returns {void}
	 */
	init () {
		this.dataLayerCheck();

		this.setLists();
		this.bindEvents();
		this.getImpressions();
		this.getPromos();
	}

	/**
	 * Bind Events that are necessary for GTM/dataLayer.
	 * @returns {void}
	 */
	bindEvents () {
		this.bindLazyLoads();
		this.addToCartEventListener();
		this.bindClickEvents();
	}

	/**
	 * Bind Click Events that are necessary for GTM/dataLayer.
	 * @returns {void}
	 */
	bindClickEvents () {
		this.bindProductClickEvents();
		this.bindEventClickEvents();
		this.bindPromoClicks();
	}

	/**
	 * Bind LazyLoad Events that are necessary for GTM/dataLayer.
	 * @returns {void}
	 */
	bindLazyLoads () {
		this.bindLazyPromos();
		this.bindLazyImpressions();
	}

	/**
	 * Bind Add to Cart Event which will send the 'addToCart' event to GTM.
	 * @returns {void}
	 */
	addToCartEventListener () {
		EventBus.addEventListener('MVPS:addToCart', async (event) => {
			if (event.detail && event.detail.success && event.detail.form) {
				const priceElem = document.getElementById('price-value');

				if (priceElem) {
					const quantityElem = event.detail.form.querySelector('input[name="Quantity"]');
					const gtmPrice = Number(priceElem.innerHTML.replace(/[^0-9.-]+/g, ''));
					let productData = this.productData(event.detail.form);

					productData.price = gtmPrice;
					productData.quantity = quantityElem ? parseInt(quantityElem.value, 10) : 1;
					productData.variant = event.detail.form.querySelector('input[name="VariantOption"]')?.value;

					/* If Variant, get the Variant Code (which is SKU from Product Options on the option) */
					const variantCode = event.detail.form.querySelector('input[name="VariantCode"]')?.value || '';
					if (variantCode) {
						productData.id = variantCode;
					}

					delete productData.position;

					this.addToCart([productData], this.context.Currency_Code);

					const {trackAddedToCart} = await import('custom-extensions/algolia/lib/insights');
					trackAddedToCart({
						currency: this.context.Currency_Code,
						objectData: [
							{
								price: productData.price,
								quantity: productData.quantity
							}
						],
						objectIDs: [event.detail.form.querySelector('input[name="objectID"]')?.value || ''],
						value: (productData.price * productData.quantity)?.toFixed?.(2)
					});
				}
			}
		});
	}

	/**
	 * Send the `addToCart` event to GTM via the dataLayer.
	 * @param {Array} products
	 * @param {String} currencyCode
	 * @returns {void}
	 */
	addToCart (products, currencyCode) {
		this.dataLayerCheck();

		this.clearEcommerce();

		let value = 0;
		for (const product of products) {
			value = product.price * product.quantity;
		}
		value = Math.round(value * 100) / 100;

		window.dataLayer.push({
			ecommerce: {
				add: {
					products: products
				},
				currencyCode: currencyCode,
				value: value
			},
			event: 'addToCart',
			/* eslint-disable camelcase */
			event_id: window.dl_event_id?.(),
			ga_event_action: 'Add To Cart',
			ga_event_category: 'Ecommerce',
			ga_event_label: '',
			ga_event_noninteraction_hit: false,
			ga_event_value: ''
			/* eslint-enable camelcase */
		});
	}

	/**
	 * Looks for elements with the `list` class. If it the attribute `data-gtm-list`, it will add that data tag to it's children (`impression` and `productClick`)
	 * @returns {void}
	 */
	setLists () {
		const elements = document.getElementsByClassName(this.classes.list);

		if (!elements || !elements.length) {
			return;
		}

		for (const element of elements) {
			const list = element.getAttribute('data-gtm-list') || this.determineList();

			if (!list) {
				continue;
			}

			const gtmSelectors = element.getElementsByClassName(this.classes.impression);
			let count = 0;

			for (const gtmSelector of gtmSelectors) {
				count++;

				if (!gtmSelector.getAttribute('data-gtm-list')) {
					gtmSelector.setAttribute('data-gtm-list', list);
				}

				if (!gtmSelector.getAttribute('data-gtm-position')) {
					gtmSelector.setAttribute('data-gtm-position', count);
				}
			}
		}
	}

	/**
	 * Deteremines what `list` is sent to GTM depending on what Screen the user is viewing
	 * @returns {String}
	 */
	determineList () {
		switch (this.context.Screen) {
			case 'CTGY':
				return `Category -${this.context.CategoryFullName}`;
			case 'SRCH':
				return 'Search Listing';
			case 'PLST':
				return 'Product Listing';
			default:
				return '';
		}
	}

	/**
	 * Creates an array of impressions that are available and sends them to GTM.
	 * @returns {void}
	 */
	getImpressions () {
		const products = document.getElementsByClassName(this.classes.impression);
		const impressionElements = [];

		for (const product of products) {
			if (product.classList.contains('lazyload') && !product.classList.contains('lazyloaded')) {
				continue;
			}

			if (product.getAttribute(this.attributes.impression)) {
				continue;
			}

			impressionElements.push(product);
		}

		this.sendImpressions(impressionElements, this.context.Currency_Code);
	}

	/**
	 * Sends an array of products as impressions to GTM
	 * @param {Array} products
	 * @param {String} currencyCode
	 * @returns {void}
	 */
	sendImpressions (products, currencyCode) {
		if (!products || !products.length) {
			return;
		}

		this.dataLayerCheck();

		const impressions = [];

		for (const product of products) {
			if (product.getAttribute(this.attributes.impression)) {
				continue;
			}

			product.setAttribute(this.attributes.impression, true);
			impressions.push(this.productData(product));
		}

		if (!impressions.length) {
			return;
		}

		this.clearEcommerce();

		window.dataLayer.push({
			ecommerce: {
				currencyCode: currencyCode,
				impressions: {
					products: impressions
				}
			},
			event: 'productImpressions',
			/* eslint-disable camelcase */
			event_id: window.dl_event_id?.(),
			ga_event_action: 'Impressions',
			ga_event_category: 'Ecommerce',
			ga_event_label: '',
			ga_event_noninteraction_hit: true,
			ga_event_value: ''
			/* eslint-enable camelcase */
		});
	}

	/**
	 * Binds the `lazybeforeunveil` event to determine if impressions should be sent
	 * @returns {void}
	 */
	bindLazyImpressions () {
		document.addEventListener('lazybeforeunveil', (e) => {
			if (!e.target.classList.contains(this.classes.impression)) {
				return;
			}

			const impression = e.target;

			if (impression.getAttribute(this.attributes.impression)) {
				return;
			}

			this.sendImpressions([impression], this.context.Currency_Code);
		});
	}

	/**
	 * Binds the Product Click Events. The product 'clicks' are taken from impressions.
	 * @returns {void}
	 */
	bindProductClickEvents () {
		const products = document.getElementsByClassName(this.classes.impression);

		for (const product of products) {
			const productData = this.productData(product);
			const productLinks = product.getElementsByTagName('a');

			if (product.nodeName === 'A') {
				this.bindProductClickEvent(product, productData);
			}

			for (const productLink of productLinks) {
				this.bindProductClickEvent(productLink, productData);
			}
		}
	}

	/**
	 * Binds the Product Click Event.
	 * @param {Element} productLink
	 * @param {Object} productData
	 * @returns {void}
	 */
	bindProductClickEvent (productLink, productData) {
		if (productLink.getAttribute(this.attributes.productClick)) {
			return;
		}

		productLink.setAttribute(this.attributes.productClick, true);
		productLink.addEventListener('click', (e) => this.productClick(e, productLink, productData));
	}

	/**
	 * On click of a product, data is sent to GTM
	 * @param {Event} event
	 * @param {Element} productLink
	 * @param {Object} productData
	 * @returns {void}
	 */
	productClick (event, productLink, productData) {
		// If GTM has not loaded in, due to being blocked, we don't want to prevent the user from clicking the link.
		if (typeof window.google_tag_manager === 'undefined') {
			return;
		}

		this.dataLayerCheck();

		const link = productLink.getAttribute('href');

		if (event && link) {
			event.preventDefault();
		}

		this.clearEcommerce();

		window.dataLayer.push({
			ecommerce: {
				click: {
					actionField: {
						list: productLink.getAttribute('data-gtm-list') || ''
					},
					products: [productData]
				}
			},
			event: 'productClick',
			eventCallback: () => {
				this.clickEventCallback(event, link);
			},
			eventTimeout: 1500,
			/* eslint-disable camelcase */
			event_id: window.dl_event_id?.(),
			ga_event_action: 'Product Click',
			ga_event_category: 'Ecommerce',
			ga_event_label: '',
			ga_event_noninteraction_hit: false,
			ga_event_value: ''
			/* eslint-enable camelcase */
		});
	}

	/**
	 * @param {Element} product
	 * @return {Object} data
	 */
	productData (product) {
		const data = {
			category: product.getAttribute('data-gtm-category') || '',
			id: product.getAttribute('data-gtm-id') || '',
			list: product.getAttribute('data-gtm-list') ? this.slugify(product.getAttribute('data-gtm-list')) : '',
			name: product.getAttribute('data-gtm-name') || '',
			position: product.getAttribute('data-gtm-position') || null,
			price: parseFloat(product.getAttribute('data-gtm-price' || 0.00))
		};

		return data;
	}

	/**
	 * If there is a link and event, it will determine if the link should open in a new tab, open in a new window, or just change the location.
	 * @param {Event} event
	 * @param {Element} link
	 * @returns {void}
	 */
	clickEventCallback (event, link) {
		if (!event || !link) {
			return;
		}

		if (event.ctrlKey || event.metaKey) {
			window.open(link, '_blank').focus();
		} else if (event.shiftKey) {
			window.open(link);
		} else {
			document.location = link;
		}
	}

	/**
	 * Binds click events for elements with the `eventClick` class.
	 * @returns {void}
	 */
	bindEventClickEvents () {
		const eventLinks = document.getElementsByClassName(this.classes.eventClick);

		for (const eventLink of eventLinks) {
			if (eventLink.getAttribute(this.attributes.eventClick)) {
				continue;
			}

			eventLink.setAttribute(this.attributes.eventClick, true);
			eventLink.addEventListener('click', (e) => this.eventClick(e, eventLink));
		}
	}

	/**
	 * Triggers an event to GTM, via a click event
	 * @param  {Event} event
	 * @param  {Element} eventLink
	 * @returns {void}
	 */
	eventClick (event, eventLink) {
		// If GTM has not loaded in, due to being blocked, we don't want to prevent the user from clicking the link.
		if (typeof window.google_tag_manager === 'undefined') {
			return;
		}

		this.dataLayerCheck();

		const link = eventLink.getAttribute('href');

		if (event && link) {
			event.preventDefault();
		}

		this.clearEcommerce();

		this.sendEvent({
			action: eventLink.getAttribute('data-gtm-action') || '',
			category: eventLink.getAttribute('data-gtm-category') || '',
			event: event,
			label: eventLink.getAttribute('data-gtm-label') || '',
			link: link,
			noninteractionHit: false,
			value: eventLink.getAttribute('data-gtm-value') || ''
		});
	}

	/**
	 * Creates an array of promotions that are available and sends them to GTM.
	 * @returns {void}
	 */
	getPromos () {
		const promos = document.getElementsByClassName(this.classes.promo);
		let promotions = [];

		for (const promo of promos) {
			if (promo.classList.contains('lazyload') && !promo.classList.contains('lazyloaded')) {
				continue;
			}

			if (promo.getAttribute(this.attributes.promo)) {
				continue;
			}

			promo.setAttribute(this.attributes.promo, true);
			promotions.push(this.promoData(promo));
		}

		if (promotions.length === 0) {
			return;
		}

		this.sendPromos(promotions);
	}

	/**
	 * Creates an array of promotions that are available and sends them to GTM.
	 * @param {Element} promo
	 * @returns {Object}
	 */
	promoData (promo) {
		return {
			/* eslint-disable camelcase */
			creative: promo.getAttribute('data-gtm-promo-creative') || '',
			creative_name: promo.getAttribute('data-gtm-promo-creative') || '',
			id: promo.getAttribute('data-gtm-promo-id') || '',
			name: promo.getAttribute('data-gtm-promo-name') || '',
			position: promo.getAttribute('data-gtm-promo-position') || '',
			promotion_id: promo.getAttribute('data-gtm-promo-id') || '',
			promotion_name: promo.getAttribute('data-gtm-promo-name') || ''
			/* eslint-enable camelcase */
		};
	}

	/**
	 * Sends an array of promotions, and sends them to GTM.
	 * @param {Array} promotions
	 * @returns {void}
	 */
	sendPromos (promotions) {
		if (promotions.length === 0) {
			return;
		}

		this.dataLayerCheck();

		this.clearEcommerce();

		window.dataLayer.push({
			ecommerce: {
				promoView: {
					promotions: promotions
				}
			},
			event: 'view_promotions',
			/* eslint-disable camelcase */
			event_id: window.dl_event_id?.()
			/* eslint-enable camelcase */
		});
	}

	/**
	 * Binds the `lazybeforeunveil` event to determine if promotion should be sent
	 * @returns {void}
	 */
	bindLazyPromos () {
		document.addEventListener('lazybeforeunveil', (e) => {
			if (!e.target.classList.contains(this.classes.promo)) {
				return;
			}

			const promo = e.target;

			if (promo.getAttribute(this.attributes.promo)) {
				return;
			}

			promo.setAttribute(this.attributes.promo, true);
			this.sendPromos([this.promoData(promo)]);
		});
	}

	/**
	 * Binds the Promo Click Events.
	 * @returns {void}
	 */
	bindPromoClicks () {
		const promoLinks = document.getElementsByClassName(this.classes.promoLink);

		for (const promoLink of promoLinks) {
			if (promoLink.getAttribute(this.attributes.promoLink)) {
				continue;
			}

			promoLink.setAttribute(this.attributes.promoLink, true);
			promoLink.addEventListener('click', (e) => this.promoClick(e, promoLink));
		}
	}

	/**
	 * Binds the Promo Click Event.
	 * @param {Event} event
	 * @param {Element} promoLink
	 * @returns {void}
	 */
	promoClick (event, promoLink) {
		// If GTM has not loaded in, due to being blocked, we don't want to prevent the user from clicking the link.
		if (typeof window.google_tag_manager === 'undefined') {
			return;
		}

		this.dataLayerCheck();

		const link = promoLink.getAttribute('href');

		if (event && link) {
			event.preventDefault();
		}

		this.clearEcommerce();

		window.dataLayer.push({
			ecommerce: {
				promoClick: {
					promotions: [this.promoData(promoLink)]
				}
			},
			event: 'promotion_click',
			eventCallback: () => {
				this.clickEventCallback(event, link);
			},
			eventTimeout: 1500,
			/* eslint-disable camelcase */
			event_id: window.dl_event_id?.(),
			ga_event_action: 'Promotion Click',
			ga_event_category: 'Ecommerce',
			ga_event_label: '',
			ga_event_noninteraction_hit: false,
			ga_event_value: ''
			/* eslint-enable camelcase */
		});
	}

	/**
	 * Sends an 'event' event to GTM
	 * @param  {Object} event
	 * @returns {void}
	 */
	sendEvent (event) {
		this.dataLayerCheck();

		window.dataLayer.push({
			event: 'ga_event',
			eventCallback: () => {
				this.clickEventCallback(event.event, event.link);
			},
			eventTimeout: 1500,
			/* eslint-disable camelcase */
			event_id: window.dl_event_id?.(),
			ga_event_action: event.action,
			ga_event_category: event.category,
			ga_event_label: event.label,
			ga_event_noninteraction_hit: event.noninteraction_hit,
			ga_event_value: event.value
			/* eslint-enable camelcase */
		});
	}

	/**
	 * Declares window.dataLayer.
	 * @returns {void}
	 */
	dataLayerCheck () {
		window.dataLayer = window.dataLayer || [];
	}

	/**
	 * Clears the `ecommerce` dataLayer object
	 * @returns {void}
	 */
	clearEcommerce () {
		this.dataLayerCheck();

		// Clear out previous Ecommerce Object.
		window.dataLayer.push({
			ecommerce: null
		});
	}

	/**
	 * Slugify a string
	 * @param {String} text
	 * @returns {void}
	 */
	slugify (text) {
		return text.toLowerCase().replace(/[^\w ]+/g, '').replace(/ +/g, '-');
	}

	// eslint-disable-next-line max-lines-per-function
	sendProductClick ({
		/* eslint-disable camelcase */
		category = '',
		id = '',
		list = '',
		name = '',
		position = '',
		price = 0.00
	}) {
		return new Promise(resolve => {
			// Ensure data layer exists and ecommerce attribute is empty
			this.clearEcommerce();

			let formattedList = list;
			if (!formattedList) {
				formattedList = this.determineList();
			}

			// Push data to GTM
			window.dataLayer.push({
				ecommerce: {
					click: {
						actionField: {
							list: formattedList
						},
						products: [
							{
								category,
								id,
								name,
								position,
								price
							}
						]
					}
				},
				event: 'productClick',
				eventCallback: () => {
					resolve();
				},
				eventTimeout: 1500,
				/* eslint-disable camelcase */
				event_id: window.dl_event_id?.(),
				ga_event_action: 'productClick',
				ga_event_category: 'Ecommerce',
				ga_event_label: '',
				ga_event_noninteraction_hit: false,
				ga_event_value: ''
				/* eslint-enable camelcase */
			});
		});
	}

	/**
	 * @param {string} list
	 * @param {any[]} product
	 */
	debouncedSendImpressions (list, product) {
		// eslint-disable-next-line object-property-newline
		const entry = debouncedImpressions[list] ?? (debouncedImpressions[list] = {products: [], timer: null});

		if (entry) {
			// Push product to array
			entry.products.push(product);

			// If the timer is defined clear the timeout
			if (entry.timer) {
				clearTimeout(entry.timer);
			}

			// Set the timer and push impressions when the timer expires
			entry.timer = setTimeout(() => {
				entry.timer = null;

				this.dataLayerCheck();
				this.clearEcommerce();

				window.dataLayer.push({
					ecommerce: {
						currencyCode: this.context.Currency_Code,
						impressions: {
							products: entry.products
						}
					},
					event: 'productImpressions',
					/* eslint-disable camelcase */
					event_id: window.dl_event_id?.(),
					ga_event_action: 'Impressions',
					ga_event_category: 'Ecommerce',
					ga_event_label: '',
					ga_event_noninteraction_hit: true,
					ga_event_value: ''
					/* eslint-enable camelcase */
				});

				// Clear products array to prevent duplicates being sent
				entry.products = [];
			}, 300);
		}
	}
}
